// Organic: Main energy service - high-level API for the application

// Import types
import { EnergyType, EnergyData, EnergyPlant, GemeindeData, GemeindeOnlineData } from '../types';

// Import atomic functions
import { createEmptyEnergyData } from '../atomic/transform';
import { createUserFriendlyError, logError } from '../atomic/errorHandling';

// Import molecular services
import { loadEnergyDataForPLZ, loadEnergyDataBatch, loadGemeindenData, loadGemeindenOnlineData, searchGemeindenOnline, convertOnlineToLegacyFormat, getIntegratedEnergyData } from '../molecular/dataService';
import { shouldUpdateData } from '../molecular/dataValidation';

// Energy type labels
const ENERGY_TYPE_LABELS: Record<EnergyType, string> = {
  wasser: 'Wasserkraft',
  sonne: 'Sonnenenergie',
  biogas: 'Biogas',
  biomasse: 'Biomasse'
};

// Initialize data coverage monitoring
let coverageCheckPerformed = false;

/**
 * Initialize the energy service and perform data coverage validation
 */
export async function initializeEnergyService(): Promise<void> {
  if (coverageCheckPerformed) return;
  
  try {
    
    // Check if data needs updating
    const needsUpdate = await shouldUpdateData();
    if (needsUpdate) {
      console.warn('[EnergyService] Data coverage below threshold or outdated - consider updating');
    }
    
    // Log coverage report in development (disabled for now - too verbose)
    // if (process.env.NODE_ENV === 'development') {
    //   await logDataCoverage();
    // }
    
    coverageCheckPerformed = true;
  } catch (error) {
    console.error('[EnergyService] Error during initialization:', error);
  }
}


/**
 * Get energy data for a specific PLZ using server-side API
 */
export async function getEnergyData(plz: string): Promise<EnergyData> {
  try {
    // Initialize service on first call
    await initializeEnergyService();
    
    // Use new API-based approach that prevents browser crashes
    const energyData = await loadEnergyDataForPLZ(plz);
    
    // Extract energy plants by type
    const sonne = energyData.sonne || [];
    const wasser = (energyData.wasser || []).filter(plant => plant.leistungKw < 10000);
    const biomasse = energyData.biomasse || [];
    const biogas = energyData.biogas || [];
    
    const totalData = sonne.length + wasser.length + biomasse.length + biogas.length;
    if (totalData === 0) {
      console.warn(`[EnergyService] No energy data found for PLZ ${plz}`);
    }
    
    return {
      sonne,
      wasser,
      biomasse,
      biogas
    };
  } catch (error) {
    logError('EnergyService.getEnergyData', error);
    const serviceError = createUserFriendlyError(error, 'data loading');
    throw new Error(serviceError.userMessage);
  }
}

/**
 * Get energy data and gemeinde info for a specific PLZ
 */
export async function getEnergyDataByPLZ(plz: string): Promise<{ energyData: EnergyData; gemeindeInfo: GemeindeData | null }> {
  try {
    // Validate PLZ format
    if (!plz || !/^\d{4}$/.test(plz)) {
      throw new Error(`Ungültige PLZ: ${plz}. Bitte geben Sie eine 4-stellige PLZ ein.`);
    }
    
    // Get energy data directly for this PLZ
    const energyData = await getEnergyData(plz);
    
    // Load gemeinde data to get info (preferring KV database)
    const gemeindeOnlineData = await loadGemeindenOnlineData();
    
    // Find which gemeinde this PLZ belongs to
    let gemeindeInfo: GemeindeData | null = null;
    
    // Build PLZ mapping from online gemeinden data
    let foundGemeindeData: GemeindeOnlineData | null = null;
    
    // Search through all gemeinden in the online data structure
    for (const [gemeindeNummer, onlineData] of Object.entries(gemeindeOnlineData)) {
      if (gemeindeNummer === 'timestamp' || gemeindeNummer === 'dataSource') continue; // Skip metadata
      
      const data = onlineData as GemeindeOnlineData;
      if (data.postleitzahlen && data.postleitzahlen.includes(plz)) {
        foundGemeindeData = data;
        gemeindeInfo = convertOnlineToLegacyFormat(gemeindeNummer, data);
        break;
      }
    }
    
    if (!foundGemeindeData) {
      console.warn(`[EnergyService] PLZ ${plz} not found in any gemeinde`);
    }
    
    return { energyData, gemeindeInfo };
  } catch (error) {
    logError('EnergyService.getEnergyDataByPLZ', error);
    // If it's already a user-friendly error, re-throw it
    if (error instanceof Error && error.message.includes('PLZ')) {
      throw error;
    }
    const serviceError = createUserFriendlyError(error, 'data loading');
    throw new Error(serviceError.userMessage);
  }
}

/**
 * Get energy data for a specific municipality (Gemeinde)
 */
export async function getEnergyDataByGemeinde(gemeindeIdOrName: string | number): Promise<EnergyData> {
  try {
    // Load Gemeinde data (preferring KV database)
    const gemeindeOnlineData = await loadGemeindenOnlineData();

    // Find the Gemeinde by ID or name in online data structure
    const searchValue = gemeindeIdOrName.toString();
    let gemeinde: GemeindeData | null = null;
    
    // Search through online data structure
    for (const [gemeindeNummer, onlineData] of Object.entries(gemeindeOnlineData)) {
      if (gemeindeNummer === 'timestamp' || gemeindeNummer === 'dataSource') continue; // Skip metadata
      
      const data = onlineData as GemeindeOnlineData;
      if (gemeindeNummer === searchValue || data.name.toLowerCase() === searchValue.toLowerCase()) {
        gemeinde = convertOnlineToLegacyFormat(gemeindeNummer, data);
        break;
      }
    }

    if (!gemeinde) {
      console.warn(`[EnergyService] Gemeinde nicht gefunden: ${gemeindeIdOrName}`);
      // Return empty energy data instead of throwing
      return createEmptyEnergyData();
    }

    // Get PLZs for this Gemeinde
    const plzs = gemeinde.plzList || [];
    if (plzs.length === 0) {
      console.warn(`[EnergyService] Keine PLZs für Gemeinde gefunden: ${gemeinde.name}`);
      // Return empty energy data instead of throwing
      return createEmptyEnergyData();
    }

    // Get energy data for all PLZs of this Gemeinde in a single batch request
    const gemeindeName = gemeinde.name;
    const sonne: EnergyPlant[] = [];
    const wasser: EnergyPlant[] = [];
    const biomasse: EnergyPlant[] = [];
    const biogas: EnergyPlant[] = [];

    try {
      // Use batch API to load all PLZ data at once (much more efficient)
      const batchEnergyData = await loadEnergyDataBatch(plzs);
      
      // Process all PLZ data from the batch response
      for (const [plz, energyDataForPlz] of Object.entries(batchEnergyData)) {
        // Filter Solar plants by Ort
        const sonneForPlz = (energyDataForPlz.sonne || []).filter(plant => 
          plant.ort && typeof plant.ort === 'string' && plant.ort.toLowerCase() === gemeindeName.toLowerCase()
        );
        sonne.push(...sonneForPlz);

        // Filter Water plants by Ort (< 10,000 kW)
        const wasserForPlz = (energyDataForPlz.wasser || [])
          .filter(plant => plant.leistungKw < 10000)
          .filter(plant => plant.ort && typeof plant.ort === 'string' && plant.ort.toLowerCase() === gemeindeName.toLowerCase());
        wasser.push(...wasserForPlz);

        // Filter Biomass plants by Ort
        const biomasseForPlz = (energyDataForPlz.biomasse || []).filter(plant => 
          plant.ort && typeof plant.ort === 'string' && plant.ort.toLowerCase() === gemeindeName.toLowerCase()
        );
        biomasse.push(...biomasseForPlz);

        // Filter Biogas plants by Ort
        const biogasForPlz = (energyDataForPlz.biogas || []).filter(plant => 
          plant.ort && typeof plant.ort === 'string' && plant.ort.toLowerCase() === gemeindeName.toLowerCase()
        );
        biogas.push(...biogasForPlz);
      }
    } catch (error) {
      console.warn(`[EnergyService] Error loading batch energy data for gemeinde ${gemeindeName}:`, error);
    }

    console.log(`[EnergyService] Gemeinde ${gemeindeName}: Found ${sonne.length} solar, ${wasser.length} water, ${biomasse.length} biomass, ${biogas.length} biogas plants by Ort filtering`);

    return {
      sonne,
      wasser,
      biomasse,
      biogas
    };
    
  } catch (error) {
    console.error('[EnergyService] Error in getEnergyDataByGemeinde:', error);
    return createEmptyEnergyData();
  }
}

/**
 * Calculate differentiated growth rates based on energy type
 * Returns realistic growth factors for 2025 projection
 */
export function getEnergyTypeGrowthFactors(): {
  solar: number;
  water: number;
  biomass: number;
  biogas: number;
} {
  return {
    solar: 1.12,   // 12% growth - reflecting strong PV expansion in Austria
    water: 1.005,  // 0.5% growth - market nearly saturated, few new sites
    biomass: 1.01, // 1% growth - limited by available biomass resources
    biogas: 1.01   // 1% growth - limited by substrate availability
  };
}

/**
 * Calculate total energy production from an array of energy plants
 * Uses 2024 data with energy type specific growth projection for 2025
 */
export function calculateTotalEnergy(plants: EnergyPlant[]): number {
  // Ensure plants is an array
  if (!Array.isArray(plants)) {
    console.warn('[EnergyService] calculateTotalEnergy received non-array:', plants);
    return 0;
  }
  
  // Conservative fallback growth rate if type cannot be determined
  const FALLBACK_GROWTH_RATE = 0.05;
  const MONTHS_INTO_2025 = 5.8; // Late May 2025
  const FALLBACK_GROWTH_FACTOR = 1 + (FALLBACK_GROWTH_RATE * (MONTHS_INTO_2025 / 12));
  
  return plants.reduce((sum, plant) => {
    // Try to find 2024 data in historical records
    const data2024 = plant.historischeErträge?.find(e => e.jahr === 2024);
    
    if (data2024 && data2024.ertragKwh > 0) {
      // Use 2024 data with growth projection
      return sum + Math.round(data2024.ertragKwh * FALLBACK_GROWTH_FACTOR);
    } else if (plant.ertragKwh > 0) {
      // Fallback to current ertragKwh (which might be 2025 data)
      // Adjust back to full-year projection if needed
      return sum + plant.ertragKwh;
    }
    
    return sum;
  }, 0);
}

/**
 * Calculate projected 2025 total energy with differentiated growth rates
 * This function provides more accurate projections than calculateTotalEnergy
 */
export function calculateProjected2025Energy(energyData: EnergyData): number {
  if (!energyData) return 0;
  
  const growthFactors = getEnergyTypeGrowthFactors();
  
  // Calculate 2024 baseline for each energy type
  const solarErtrag = calculateTotalEnergy(energyData.sonne || []);
  const wasserErtrag = calculateTotalEnergy(energyData.wasser || []);
  const biomasseErtrag = calculateTotalEnergy(energyData.biomasse || []);
  const biogasErtrag = calculateTotalEnergy(energyData.biogas || []);
  
  // Apply differentiated growth rates
  const projected2025 = (
    solarErtrag * growthFactors.solar +
    wasserErtrag * growthFactors.water +
    biomasseErtrag * growthFactors.biomass +
    biogasErtrag * growthFactors.biogas
  );
  
  return Math.round(projected2025);
}

/**
 * Get display label for an energy type
 */
export function getEnergyTypeLabel(type: string): string {
  return ENERGY_TYPE_LABELS[type as EnergyType] || type;
}

/**
 * Get all available energy types
 */
export function getEnergyTypes(): { key: EnergyType; label: string }[] {
  return Object.entries(ENERGY_TYPE_LABELS).map(([key, label]) => ({
    key: key as EnergyType,
    label
  }));
}

/**
 * Calculate Tirol-wide average energy potential per Gemeinde using pre-calculated data
 * This avoids hundreds of API calls by using integrated energy data from gemeinden_online.json
 */
export async function calculateTirolAverage(): Promise<number> {
  try {
    const gemeindeOnlineData = await loadGemeindenOnlineData();
    if (!gemeindeOnlineData) return 0;

    let totalPotential = 0;
    let validGemeinden = 0;

    // Use pre-calculated energy data from gemeinden_online.json instead of API calls
    for (const [gemeindeNummer, onlineData] of Object.entries(gemeindeOnlineData)) {
      if (gemeindeNummer === 'timestamp' || gemeindeNummer === 'dataSource') continue; // Skip metadata
      
      const data = onlineData as GemeindeOnlineData;
      
      try {
        // Use integrated energy data (no API calls needed)
        const energyData = getIntegratedEnergyData(data);
        
        const totalErtrag = energyData.solarKwh + energyData.waterKwh + energyData.biomassKwh + energyData.biogasKwh;
        const handelbareFaktor = 0.4;
        const preisProKwh = 0.106;
        const potential = totalErtrag * handelbareFaktor * preisProKwh;
        
        if (potential > 0) {
          totalPotential += potential;
          validGemeinden++;
        }
      } catch (error) {
        // Skip gemeinden that have issues
        console.warn(`Skipping gemeinde ${gemeindeNummer}:`, error);
      }
    }

    console.log(`[EnergyService] Calculated Tirol average from ${validGemeinden} gemeinden using pre-calculated data (no API calls)`);
    return validGemeinden > 0 ? Math.round(totalPotential / validGemeinden) : 0;
  } catch (error) {
    console.error('[EnergyService] Error calculating Tirol average:', error);
    return 0;
  }
}

// Export all public functions
export * from '../types';
export { createEmptyEnergyData } from '../atomic/transform';
export { loadGemeindenData, loadGemeindenOnlineData, searchGemeindenOnline, convertOnlineToLegacyFormat, getIntegratedEnergyData, loadEnergyDataBatch } from '../molecular/dataService';
export type { EnergyData, GemeindeOnlineData } from '../types';
